import { emptySplitApi as api } from "./baseApi";
import type { BalancedScorecard } from '../components/reviews/bscTypes';

export const addTagTypes = ["BalancedScorecardController"] as const;

const balancedScorecardApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // Get all BSC submissions for current user
      getMyBSCSubmissions: build.query<BalancedScorecard[], void>({
        query: () => ({ url: `/balanced-scorecards/my-submissions` }),
        providesTags: ["BalancedScorecardController"],
      }),

      // Get BSCs pending review from direct reports
      getPendingReviewBSCs: build.query<BalancedScorecard[], void>({
        query: () => ({ url: `/balanced-scorecards/pending-review` }),
        providesTags: ["BalancedScorecardController"],
      }),

      // Get all BSCs from direct reports (all statuses)
      getTeamBSCSubmissions: build.query<BalancedScorecard[], void>({
        query: () => ({ url: `/balanced-scorecards/team-submissions` }),
        providesTags: ["BalancedScorecardController"],
      }),

      // Get BSC by ID
      getBSCById: build.query<BalancedScorecard, { id: string }>({
        query: (queryArg) => ({ url: `/balanced-scorecards/${queryArg.id}` }),
        providesTags: ["BalancedScorecardController"],
      }),

      // Get BSC submissions by period
      getBSCByPeriod: build.query<BalancedScorecard[], { period: string }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/by-period/${queryArg.period}`,
        }),
        providesTags: ["BalancedScorecardController"],
      }),

      // Get BSC submissions for a specific user (admin)
      getBSCsByUser: build.query<BalancedScorecard[], { userId: string }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/by-user/${queryArg.userId}`,
        }),
        providesTags: ["BalancedScorecardController"],
      }),

      // Create new BSC (also used for submitting - just set status: 'submitted')
      createBSC: build.mutation<BalancedScorecard, Partial<BalancedScorecard>>({
        query: (body) => ({
          url: `/balanced-scorecards`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["BalancedScorecardController"],
      }),

      // Update BSC
      updateBSC: build.mutation<void, { id: string; body: Partial<BalancedScorecard> }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.body,
        }),
        invalidatesTags: ["BalancedScorecardController"],
      }),

      // Submit existing BSC for review (changes status to submitted)
      submitBSC: build.mutation<BalancedScorecard, Partial<BalancedScorecard>>({
        query: (body) => ({
          url: `/balanced-scorecards`,
          method: "POST",
          body: { ...body, status: 'submitted' },
        }),
        invalidatesTags: ["BalancedScorecardController"],
      }),

      // Approve BSC
      approveBSC: build.mutation<BalancedScorecard, { id: string; managerComments?: string }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/${queryArg.id}/approve`,
          method: "PATCH",
          body: { managerComments: queryArg.managerComments },
        }),
        invalidatesTags: ["BalancedScorecardController"],
      }),

      // Reject BSC
      rejectBSC: build.mutation<BalancedScorecard, { id: string; managerComments: string }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/${queryArg.id}/reject`,
          method: "PATCH",
          body: { managerComments: queryArg.managerComments },
        }),
        invalidatesTags: ["BalancedScorecardController"],
      }),

      // Revert BSC to draft (only for submitted, not yet approved)
      revertBSCToDraft: build.mutation<BalancedScorecard, { id: string }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/${queryArg.id}/revert-to-draft`,
          method: "PATCH",
        }),
        invalidatesTags: ["BalancedScorecardController"],
      }),

      // Get org chart with BSC scores
      getOrgChartWithBSC: build.query<OrgChartNode[], { period?: string }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/org-chart`,
          params: queryArg.period ? { period: queryArg.period } : undefined,
        }),
        providesTags: ["BalancedScorecardController"],
      }),

      // Get direct reports with BSC for a specific JD
      getDirectReportsWithBSC: build.query<OrgChartReportsResponse, { jobDescriptionId: string; period?: string }>({
        query: (queryArg) => ({
          url: `/balanced-scorecards/org-chart/${queryArg.jobDescriptionId}/reports`,
          params: queryArg.period ? { period: queryArg.period } : undefined,
        }),
        providesTags: ["BalancedScorecardController"],
      }),
    }),
    overrideExisting: false,
  });

// Types for org chart
export interface OrgChartEmployee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  fullName: string;
}

export interface OrgChartJD {
  id: string;
  title: string;
  department?: string;
  businessUnit?: string;
  reportsToJobDescriptionId?: string;
  directReportsCount: number;
  indirectReportsCount: number;
}

export interface OrgChartBSC {
  id: string;
  reviewPeriod: string;
  totalWeightedScore?: number;
  finalGrade?: string;
  bonusEntitlement?: number;
  status: string;
  submittedAt?: string;
}

export interface OrgChartNode {
  jobDescription: OrgChartJD;
  employee: OrgChartEmployee | null;
  latestBSC: OrgChartBSC | null;
  directReportsCount: number;
  hasDirectReports?: boolean;
}

export interface OrgChartReportsResponse {
  parent: OrgChartNode;
  directReports: OrgChartNode[];
}

export { balancedScorecardApi };

export const {
  useGetMyBSCSubmissionsQuery,
  useLazyGetMyBSCSubmissionsQuery,
  useGetPendingReviewBSCsQuery,
  useGetTeamBSCSubmissionsQuery,
  useGetBSCByIdQuery,
  useLazyGetBSCByIdQuery,
  useGetBSCByPeriodQuery,
  useGetBSCsByUserQuery,
  useCreateBSCMutation,
  useUpdateBSCMutation,
  useSubmitBSCMutation,
  useApproveBSCMutation,
  useRejectBSCMutation,
  useRevertBSCToDraftMutation,
  useGetOrgChartWithBSCQuery,
  useGetDirectReportsWithBSCQuery,
  useLazyGetDirectReportsWithBSCQuery,
} = balancedScorecardApi;
